import { Injectable, OnDestroy, OnInit } from '@angular/core'
import {
  Country,
  Currency,
  GlobalMessageService,
  GlobalMessageType,
  SemanticPathService,
  WindowRef
} from '@spartacus/core'
import { CountryLanguageService } from '../../site-context/facade/country-language.service'
import { CountryService } from '../../site-context/facade/country.service'
import { Subscription } from 'rxjs'
import { MbConstants } from '../../shared/mb-constants'
import { MbCheckoutNavigationService } from '../../checkout/services/mb-checkout-navigation.service'
import { Actions, ofType } from '@ngrx/effects'
import { MbCartActions } from '../store/actions'
import { filter, map } from 'rxjs/operators'
import { CookieKeys } from '../../cms/enums/cookie-keys'
import { CookieService } from 'ngx-cookie-service'
import { Router } from '@angular/router'

@Injectable({
  providedIn: 'root'
})
export class MbAfterCartMigrationService implements OnInit, OnDestroy {

  subscription: Subscription = new Subscription()
  userId: string | undefined
  cartId: string | undefined
  constants = MbConstants
  activeSiteCountryIso: string | undefined
  activeSiteCountryName: string | undefined

  constructor (protected countryLanguageService: CountryLanguageService,
              protected countryService: CountryService,
              protected mbCheckoutNavigationService: MbCheckoutNavigationService,
              protected globalMessageService: GlobalMessageService,
              protected actions$: Actions,
              protected cookieService: CookieService,
              protected winRef: WindowRef,
              protected router: Router,
              protected semanticPathService: SemanticPathService) {
  }

  // eslint-disable-next-line @angular-eslint/contextual-lifecycle
  ngOnInit (): void {
    this.subscription.add(this.countryService.getActive().subscribe(activeSiteCountryIso => {
      this.activeSiteCountryIso = activeSiteCountryIso
    }))
    this.subscription.add(this.countryService.getActiveCountryName().subscribe(activeCountryName => {
      this.activeSiteCountryName = activeCountryName
    }))
    this.subscription?.add(this.actions$.pipe(
      ofType(MbCartActions.MIGRATE_CART_SUCCESS),
      map((action: MbCartActions.MigrateCartSuccess) => action?.payload),
      filter((payload: any) => payload?.isLoginProcess))
      .subscribe((payload: any) => {
        if (payload?.migratedCart) {
          const newBaseSite: string | undefined =
            this.countryLanguageService.getBaseSiteForCountry(payload?.user?.country?.isocode)
          if (!payload?.userHasNoCart) {
            this.mapNewCartGuidToNewBaseSite(payload.migratedCart.code,
              newBaseSite || this.constants.MB_BASE_SITE_NO_COUNTRY_KEY + this.constants.FALLBACK_COUNTRY)
          }
          this.changeActiveBaseSiteAfterLogin(payload?.user?.country, payload?.user?.currency, payload?.userHasNoCart)
        } else {
          this.globalMessageService.add({ key: 'quoteInfo.error' }, GlobalMessageType.MSG_TYPE_ERROR)
        }
      }))
  }

  changeActiveBaseSiteAfterLogin (userCountry: Country, userCurrency: Currency, userHasNoCart: boolean = false): void {
    if (userCountry?.isocode !== this.activeSiteCountryIso) {
      const newBaseSite: string | undefined = this.countryLanguageService.getBaseSiteForCountry(userCountry?.isocode)
      const hasBaseSite: boolean = !!newBaseSite

      const sourceCountryName: string | undefined = this.activeSiteCountryIso !== this.constants.FALLBACK_COUNTRY ?
        this.activeSiteCountryName : this.constants.INTERNATIONAL
      const targetCountryName: string | undefined = userCountry?.name
      if (hasBaseSite && userCountry?.isocode) {
        this.countryLanguageService.setActive(userCountry?.isocode, userCurrency?.isocode)
        this.updateLocationCookies(userCountry?.isocode)
        if (userHasNoCart) {
          this.router.navigate([this.semanticPathService.get('myAccount')])
            .catch((error: Error) => console.error(error))
        } else {
          this.mbCheckoutNavigationService.navigateToCartPage({
            key: 'mbcart.cartMigrationMessage',
            params: { sourceCountry: sourceCountryName, targetCountry: targetCountryName }
          })
        }
      } else if (!hasBaseSite) {
        this.countryLanguageService.setActive(this.constants.FALLBACK_COUNTRY, this.constants.FALLBACK_CURRENCY)
        this.updateLocationCookies(this.constants.FALLBACK_COUNTRY)
        this.router.navigate([this.semanticPathService.get('myAccount')])
          .catch((error: Error) => console.error(error))
      }
      this.winRef.localStorage?.removeItem(this.constants.CART_MIGRATION_SPARTACUS_KEY + 'redirectUrl')
    } else {
      const continueToCheckout: boolean = this.winRef.localStorage?.getItem(this.constants.CART_MIGRATION_SPARTACUS_KEY + 'redirectUrl') === 'true'
      this.winRef.localStorage?.removeItem(this.constants.CART_MIGRATION_SPARTACUS_KEY + 'redirectUrl')
      if (continueToCheckout) {
        this.mbCheckoutNavigationService.navigateToNextCheckoutStep(true)
      } else {
        this.router.navigate([this.semanticPathService.get('myAccount')])
          .catch((error: Error) => console.error(error))
      }
    }
  }

  mapNewCartGuidToNewBaseSite (migratedCartGuid: string, newBaseSite: string): void {
    if (newBaseSite !== this.constants.MB_BASE_SITE_NO_COUNTRY_KEY + this.activeSiteCountryIso) {
      this.winRef.localStorage?.setItem(
        this.constants.CART_MIGRATION_SPARTACUS_KEY + newBaseSite + this.constants.CART_MIGRATION_KEY, '{"active":"' + migratedCartGuid + '"}')
      this.winRef.localStorage?.setItem(
        this.constants.CART_MIGRATION_SPARTACUS_KEY + this.constants.MB_BASE_SITE_NO_COUNTRY_KEY + this.activeSiteCountryIso
        + this.constants.CART_MIGRATION_KEY, '{"active":""}')
    }
  }

  updateLocationCookies (country: string): void {
    this.cookieService.set(CookieKeys.NAVIGATION_COUNTRY, country,{ path: '/' })
    this.cookieService.set(CookieKeys.MILTENYI_SELECT_COUNTRY, country, { path: '/' })
    this.cookieService.set(CookieKeys.MILTENYI_COUNTRY, country, { path: '/' })
  }

  ngOnDestroy (): void {
    this.subscription?.unsubscribe()
  }
}
